import API_URL from '../apiconfig';
import axios from './api';
import client from '../utils/react-query/client';
import {
  CreateOffboardingPayload,
  ListOffboardingsParams,
  Offboarding,
  OffboardingMutationResult,
  UpdateOffboardingPayload,
} from '../types/offboardings';

export const offboardingsListKey = (params: ListOffboardingsParams) =>
  ['offboardings', params] as const;

export const offboardingKey = (offboardingId: string) =>
  ['offboarding', offboardingId] as const;

export async function listOffboardings(
  params: ListOffboardingsParams,
): Promise<Offboarding[]> {
  const response = await axios.get(`${API_URL}/v1/offboardings`, { params });
  return response.data.result;
}

export async function getOffboarding(
  offboardingId: string,
  params: { includeSourceCampaign?: boolean } = {},
): Promise<Offboarding> {
  const response = await axios.get(
    `${API_URL}/v1/offboardings/${offboardingId}`,
    { params },
  );
  return response.data.result;
}

export async function createOffboarding(
  payload: CreateOffboardingPayload,
): Promise<Offboarding> {
  const response = await axios.post(`${API_URL}/v1/offboardings`, payload);
  return response.data.result;
}

export async function updateOffboarding(
  offboardingId: string,
  payload: UpdateOffboardingPayload,
): Promise<OffboardingMutationResult> {
  const response = await axios.patch(
    `${API_URL}/v1/offboardings/${offboardingId}`,
    payload,
  );
  return response.data.result;
}

export async function cancelOffboarding(
  offboardingId: string,
): Promise<OffboardingMutationResult> {
  const response = await axios.delete(
    `${API_URL}/v1/offboardings/${offboardingId}`,
  );
  return response.data.result;
}

// Used by campaign mutations: the single-offboarding GET embeds the linked
// campaign + Pocketflows source. List queries don't include campaign data,
// so they don't need to refetch on a campaign change.
export async function invalidateOffboardingCampaigns() {
  await client.invalidateQueries({ queryKey: ['offboarding'] });
}

export async function invalidateOffboardings(
  businessId: string,
  offboardingId?: string,
) {
  await Promise.all([
    // refetchType: 'all' refetches inactive queries too (e.g. TeamPage's list
    // when we invalidate from the wizard) so the cache is hot before the user
    // navigates back and clicks around.
    client.invalidateQueries({
      queryKey: ['offboardings'],
      refetchType: 'all',
      predicate: query => {
        const params = query.queryKey[1] as unknown as
          | ListOffboardingsParams
          | undefined;
        return params?.businessId === businessId;
      },
    }),
    offboardingId
      ? client.invalidateQueries({
          queryKey: offboardingKey(offboardingId),
          refetchType: 'all',
        })
      : undefined,
  ]);
}
